import type { CprModificationTool } from 'config/config';
import type { ToolId } from 'config/toolDefinitionsConfig';
import { ModifierType } from '../../segmentManager';
import styles from '../SegmentManagerList.module.scss';
import Button from '@components/Button';
import Slider from '@components/Slider';
import SliderMulti from '@components/SliderMulti';
import {
  getMouseTooltipTriggerProps,
  type MouseTooltipHandlers,
} from '@components/Tooltip/useMouseTooltip';
import { handleEditModeAction } from '@features/view/Modification';
import { configStore } from 'config/configStore';
import { Connect2DSelectionState } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import type { SegmentManagerIcons } from './segmentManagerIcons';

type ToolButtonConfig = {
  id?: ToolId;
  key: string;
  label: string;
  icon?: JSX.Element;
  active: boolean;
  disabled: boolean;
  shortcut: string;
  className?: string;
  onClick?: () => void;
};

const WHEEL_STEP = 1;

const clampValue = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const getWheelStep = (deltaY: number) => {
  if (deltaY === 0) return 0;
  return Math.sign(-deltaY) * WHEEL_STEP;
};

const handleWheelStep = (
  event: React.WheelEvent<HTMLElement>,
  value: number,
  min: number,
  max: number,
  onChange: (value: number) => void,
) => {
  const wheelStep = getWheelStep(event.deltaY) * (event.shiftKey ? 10 : 1);
  if (wheelStep === 0) return;

  // event.preventDefault();
  event.stopPropagation();

  const nextValue = clampValue(value + wheelStep, min, max);
  if (nextValue !== value) {
    onChange(nextValue);
  }
};

const ENABLE_SIZE_TOOLTIP = true;
const sizeToolTipText = ENABLE_SIZE_TOOLTIP
  ? 'Mouse wheel: ±1 size step\nShift + Mouse wheel: ±10 size steps'
  : '';

type Props = {
  manualMode: boolean;
  showCprModificationTools: boolean;
  canUndoSelectedModification: boolean;
  canRedoSelectedModification: boolean;
  canApplyModification: boolean;
  canSaveDraft: boolean;
  modifierType: ModifierType;
  activeToolId?: ToolId;
  invisibleToolIds: ToolId[];
  hasVR: boolean;
  selectedPanelComponentKey?: string;
  brushStep: number;
  startValue: number;
  endValue: number;
  huMin: number;
  huMax: number;
  cprModificationTool: CprModificationTool;
  manual3DTool: {
    eraser: boolean;
    initialPoint: boolean;
  };
  cprSculptSize: number;
  cprPixelBrushMode: 'draw' | 'erase' | string;
  cprPixelBrushSize: number;
  isSelected2DPanel: boolean;
  connect2DStatus: Connect2DSelectionState;
  apply3D: boolean;
  icons: SegmentManagerIcons;
  onUndo: () => void;
  onRedo: () => void;
  onManualToolEnter: (toolId: ToolId) => void;
  onBrushStepChange: (value: number) => void;
  onBrushThresholdChange: (start: number, end: number) => void;
  onOpenManualDiscard: () => void;
  onOpenManualApply: () => void;
  onSaveDraft: () => void;
  onLoadDraft: () => void;
  onCprModificationToolToggle: (
    tool: Exclude<CprModificationTool, 'none'>,
  ) => void;
  onCprSculptSizeChange: (value: number) => void;
  onCprPixelBrushModeChange: (mode: 'draw' | 'erase') => void;
  onCprPixelBrushSizeChange: (value: number) => void;
  tooltipHandlers: MouseTooltipHandlers;
};

const SizeControl = ({
  value,
  min,
  max,
  onChange,
  tooltipHandlers,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  tooltipHandlers: MouseTooltipHandlers;
}) => {
  const handleWheel = (event: React.WheelEvent<HTMLElement>) =>
    handleWheelStep(event, value, min, max, onChange);
  const tooltipProps = getMouseTooltipTriggerProps(
    sizeToolTipText,
    tooltipHandlers,
  );

  return (
    <div className={styles.sizeContainer}>
      <div className={styles.sizeRow}>
        <span className={styles.sizeLabelContainer}>
          Size
          <span className={styles.shortcutWrapper}>
            (<span className={styles.shortcutKey}>{`← / → `}</span>) (
            <span className={styles.shortcutKey}>{`↑ / ↓`}</span>)
          </span>
        </span>
        <input
          className={styles.sizeValueContainer}
          type="number"
          value={value}
          min={min}
          max={max}
          onWheel={handleWheel}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </div>

        <Slider
          min={min}
          max={max}
          value={value}
          onWheel={handleWheel}
          onChange={onChange}
          {...tooltipProps}
        />
    </div>
  );
};

export default function ModificationToolPanel({
  manualMode,
  showCprModificationTools,
  canUndoSelectedModification,
  canRedoSelectedModification,
  canApplyModification,
  canSaveDraft,
  modifierType,
  activeToolId,
  invisibleToolIds,
  hasVR,
  selectedPanelComponentKey,
  brushStep,
  startValue,
  endValue,
  huMin,
  huMax,
  cprModificationTool,
  manual3DTool,
  cprSculptSize,
  cprPixelBrushMode,
  cprPixelBrushSize,
  isSelected2DPanel,
  connect2DStatus,
  apply3D,
  icons,
  onUndo,
  onRedo,
  onManualToolEnter,
  onBrushStepChange,
  onBrushThresholdChange,
  onOpenManualDiscard,
  onOpenManualApply,
  onSaveDraft,
  onLoadDraft,
  onCprModificationToolToggle,
  onCprSculptSizeChange,
  onCprPixelBrushModeChange,
  onCprPixelBrushSizeChange,
  tooltipHandlers,
}: Props) {
  const {
    Undo: UndoIcon,
    Redo: RedoIcon,
    Brush: BrushIcon,
    Eraser: EraserIcon,
    close: CloseIcon,
    circleCheck: CircleCheckIcon,
    Dilate: DilateIcon,
    Erode: ErodeIcon,
  } = icons;
  const showCPRTool = selectedPanelComponentKey === 'CPR';
  const isSurface3D = selectedPanelComponentKey === 'Surface3D';
  const isBrushTool = modifierType === ModifierType.BRUSH;
  const isEraserTool = modifierType === ModifierType.ERASER;

  const tooltipProps = (label: string) =>
    getMouseTooltipTriggerProps(label, tooltipHandlers);
  const handleBrushStepWheel = (event: React.WheelEvent<HTMLElement>) =>
    handleWheelStep(event, brushStep, 1, 10, onBrushStepChange);
  const handleHuMinWheel = (event: React.WheelEvent<HTMLElement>) =>
    handleWheelStep(event, startValue, huMin, endValue, (value) =>
      onBrushThresholdChange(value, endValue),
    );
  const handleHuMaxWheel = (event: React.WheelEvent<HTMLElement>) =>
    handleWheelStep(event, endValue, startValue, huMax, (value) =>
      onBrushThresholdChange(startValue, value),
    );
  const handleHuThresholdSliderWheel = (
    event: React.WheelEvent<HTMLDivElement>,
  ) => {
    const wheelStep = getWheelStep(event.deltaY);
    if (wheelStep === 0 || huMax === huMin) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;

    const pointerRatio = clampValue(
      (event.clientX - rect.left) / rect.width,
      0,
      1,
    );
    const startRatio = clampValue((startValue - huMin) / (huMax - huMin), 0, 1);
    const endRatio = clampValue((endValue - huMin) / (huMax - huMin), 0, 1);

    if (pointerRatio <= startRatio) {
      handleHuMinWheel(event);
      return;
    }

    if (pointerRatio >= endRatio) {
      handleHuMaxWheel(event);
    }
  };

  const renderButtonGroup = (
    buttons: ToolButtonConfig[],
    buttonClass = 'segment-manager-button-hug-icon20',
  ) => (
    <div className={styles.modiContainer1}>
      <div
        className={`${styles.modiContainer2} ${
          isSurface3D ? styles.surface3DToolButtonGrid : ''
        }`}
      >
        {buttons.map(
          ({ id, key, label, icon, active, disabled, shortcut, onClick }) => (
            <Button
              key={key}
              className={`${styles.compactPanelButton} ${buttonClass}${
                active ? ' active' : ''
              }`}
              aria-label={label}
              disabled={disabled}
              onClick={onClick ?? (() => id && onManualToolEnter(id))}
              startIcon={icon}
            >
              {label}
              {shortcut && (
                <span className={styles.shortcutWrapper}>
                  (<span className={styles.shortcutKey}>{shortcut}</span>)
                </span>
              )}
            </Button>
          ),
        )}
      </div>
    </div>
  );

  const renderToolButtons = () => {
    const buttons: ToolButtonConfig[] = [];

    if (isSelected2DPanel) {
      buttons.push({
        id: 'Brush',
        key: 'Brush',
        label: 'Brush',
        shortcut: 'B',
        icon: <BrushIcon />,
        active: isBrushTool || activeToolId === 'Brush',
        disabled: invisibleToolIds.includes('Brush') || hasVR,
      });
    }

    buttons.push({
      id: 'Eraser',
      key: 'Eraser',
      label: isSurface3D ? 'Draw' : 'Eraser',
      shortcut: isSurface3D ? 'Shift ' : 'E',
      icon: isSurface3D ? <BrushIcon /> : <EraserIcon />,
      active: isSurface3D
        ? manual3DTool.eraser
        : isEraserTool || activeToolId === 'Eraser',
      disabled: invisibleToolIds.includes('Eraser') || hasVR,
      onClick: isSurface3D ? () => {} : undefined,
    });

    if (isSurface3D) {
      buttons.push({
        key: 'Cut',
        label: 'Cut',
        shortcut: 'Enter',
        icon: <EraserIcon />,
        active: false,
        disabled: hasVR || !apply3D,
        onClick: () =>
          handleEditModeAction('commit', selectedPanelComponentKey),
      });
      buttons.push({
        key: 'Dilate',
        label: 'Dilate',
        shortcut: 'D',
        icon: <DilateIcon />,
        active: false,
        disabled: hasVR || !apply3D,
        onClick: () =>
          handleEditModeAction('dilate', selectedPanelComponentKey),
      });
      buttons.push({
        key: 'Erode',
        label: 'Erode',
        shortcut: 'E',
        icon: <ErodeIcon />,
        active: false,
        disabled: hasVR || !apply3D,
        onClick: () => handleEditModeAction('erode', selectedPanelComponentKey),
      });
    }

    return renderButtonGroup(buttons);
  };

  const renderCPRButtons = () => {
    const buttons: ToolButtonConfig[] = [
      {
        key: 'draw',
        label: 'Brush',
        shortcut: 'B',
        icon: <BrushIcon />,
        active:
          cprPixelBrushMode === 'draw' &&
          configStore.get().cprModificationTool === 'brush',
        disabled: false,
        onClick: () => {
          if (configStore.get().cprModificationTool !== 'brush')
            onCprModificationToolToggle('brush');
          onCprPixelBrushModeChange('draw');
        },
      },
      {
        key: 'erase',
        label: 'Erase',
        shortcut: 'E',
        icon: <EraserIcon />,
        active:
          cprPixelBrushMode === 'erase' &&
          configStore.get().cprModificationTool === 'brush',
        disabled: false,
        onClick: () => {
          if (configStore.get().cprModificationTool !== 'brush')
            onCprModificationToolToggle('brush');
          onCprPixelBrushModeChange('erase');
        },
      },
    ];

    return renderButtonGroup(buttons);
  };

  const renderModificationOptions = () => (
    <div className={styles.modiOptions}>
      {(isBrushTool || isEraserTool) && isSelected2DPanel && (
        <div className={styles.sizeContainer}>
          <div className={styles.sizeRow}>
            <span className={styles.sizeLabelContainer}>
              {`Size`}
              <span className={styles.shortcutWrapper}>
                (<span className={styles.shortcutKey}>{`← / → `}</span>) (
                <span className={styles.shortcutKey}>{`↑ / ↓`}</span>)
              </span>
            </span>
            <input
              className={styles.sizeValueContainer}
              type="number"
              disabled={false}
              value={brushStep}
              min={1}
              max={10}
              onWheel={handleBrushStepWheel}
              onChange={(event) =>
                onBrushStepChange(Number(event.target.value))
              }
              {...tooltipProps(sizeToolTipText)}
            />
          </div>
          <Slider
            min={1}
            max={10}
            value={brushStep}
            onWheel={handleBrushStepWheel}
            onChange={onBrushStepChange}
            {...tooltipProps(sizeToolTipText)}
          />
        </div>
      )}
      {isBrushTool && isSelected2DPanel && (
        <div className={`${styles.sizeContainer} ${styles.huThresholdBlock}`}>
          <div className={styles.HuRow}>
            <span className={styles.sizeLabelContainer}>
              HU Threshold
              <br />
              [Hold Shift/Tab]
            </span>
            <div className={styles.HuValueContainer}>
              <input
                className={styles.HuMinValueContainer}
                type="number"
                disabled={false}
                value={startValue}
                onWheel={handleHuMinWheel}
                onChange={(event) =>
                  onBrushThresholdChange(Number(event.target.value), endValue)
                }
              {...tooltipProps(sizeToolTipText)}
              />
              <span className={styles.HuSeparatorContainer}>{`~`}</span>
              <input
                className={styles.HuMaxValueContainer}
                type="number"
                disabled={false}
                value={endValue}
                onWheel={handleHuMaxWheel}
                onChange={(event) =>
                  onBrushThresholdChange(startValue, Number(event.target.value))
                }
                {...tooltipProps(sizeToolTipText)}
              />
            </div>
          </div>

          <div
            className={`${styles.sliderHitArea} ${styles.huThresholdSlider}`}
            onWheel={handleHuThresholdSliderWheel}
          >
            <SliderMulti
              startValue={startValue}
              endValue={endValue}
              min={huMin}
              max={huMax}
              onChange={onBrushThresholdChange}
              {...tooltipProps(sizeToolTipText)}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderToolCPRButton = (
    tool: 'line' | 'sculpt' | 'brush',
    label: string,
    Icon: React.ElementType,
  ) => (
    <Button
      className={`${styles.compactPanelButton} segment-manager-button-hug-icon20${
        cprModificationTool === tool ? ' active' : ''
      }`}
      aria-label={label}
      title={label}
      aria-pressed={cprModificationTool === tool}
      onClick={() => onCprModificationToolToggle(tool)}
      startIcon={<Icon />}
    >
      {label}
    </Button>
  );

  const ConnectStateBadges = ({
    start,
    end,
  }: {
    start: unknown;
    end: unknown;
  }) => (
    <div className={styles.pointStateRowCompact}>
      <span className={styles.modiActionsLabel}>2D Region Connect</span>

      {(['Start', 'End'] as const).map((label) => {
        const active = label === 'Start' ? start : end;

        return (
          <span
            key={label}
            className={`${styles.pointStateBadge2} ${
              active ? styles.pointStateBadgeActive : ''
            }`}
          >
            <span className={styles.pointStateDot} />
            {label}
          </span>
        );
      })}
    </div>
  );

  const renderNormalModificationTool = () => (
    <div className={styles.modiContainer}>
      <div className={styles.modiContainer}>
        {renderToolButtons()}
        {activeToolId === 'Brush' && (
          <ConnectStateBadges
            start={connect2DStatus.start}
            end={connect2DStatus.end}
          />
        )}{' '}
        {isSurface3D && showCprModificationTools && (
          <label className={styles.huCheckRow}>
            <input
              type="checkbox"
              checked={manual3DTool.initialPoint}
              onChange={() => {
                scheduleRender('Surface3D', {
                  type: 'FFR_Manual_3D',
                  mode: 'InitialPointVisibility',
                  visibility: !manual3DTool.initialPoint,
                });
              }}
            />
            <span>initialPoint</span>
          </label>
        )}
        {renderModificationOptions()}
      </div>
    </div>
  );

  const renderCPRModificationTool = () => (
    <div className={styles.modiContainer}>
      <div className={styles.modiContainer}>{renderCPRButtons()}</div>
      {cprModificationTool === 'brush' && (
        <SizeControl
          value={cprPixelBrushSize}
          min={1}
          max={30}
          onChange={onCprPixelBrushSizeChange}
          tooltipHandlers={tooltipHandlers}
        />
      )}
    </div>
  );

  return (
    <>
      <div className={styles.divider} />
      <div className={styles.toolContainer}>
        {`Modification Tool`}
        <div className={styles.frame21}>
          <div className={styles.frame20}>
            {(manualMode || showCprModificationTools) && (
              <div className={styles.modiContainer}>
                {renderButtonGroup(
                  [
                    {
                      key: 'undo',
                      label: 'Undo',
                      icon: <UndoIcon />,
                      disabled: !canUndoSelectedModification,
                      shortcut: 'Z',
                      active: false,
                      onClick: onUndo,
                    },

                    {
                      key: 'redo',
                      label: 'Redo',
                      icon: <RedoIcon />,
                      disabled: !canRedoSelectedModification,
                      shortcut: 'X',
                      active: false,
                      className: 'segment-manager-button-hug',
                      onClick: onRedo,
                    },
                  ],
                  'segment-manager-button-hug',
                )}
              </div>
            )}
            {showCPRTool
              ? renderCPRModificationTool()
              : renderNormalModificationTool()}
            {/* {showCPRTool && (
              <>
                <div className={styles.modiContainer}>
                  <div className={styles.modiActions}>
                    <div className={styles.modiActions2}>
                      <span className={styles.modiActionsLabel}>
                        CPR Modification
                      </span>

                      <div className={styles.modiContainer1}>
                        <div className={styles.modiContainer2}>
                          {renderToolCPRButton('line', 'Line', EditIcon)}
                          {renderToolCPRButton('sculpt', 'Sculpt', SculptIcon)}
                          {renderToolCPRButton('brush', 'Brush', BrushIcon)}
                        </div>
                      </div>
                    </div>
                  </div>
                  {cprModificationTool === 'sculpt' && (
                    <div className={styles.modiOptions}>
                        <SizeControl
                          value={cprSculptSize}
                          min={1}
                          max={20}
                          onChange={onCprSculptSizeChange}
                        />
                    </div>
                  )}

                  {
                    <div className={styles.modiOptions}>
                      <div className={styles.sizeContainer}>
                        <div className={styles.modiContainer2}>
                          <Button
                            className={`${styles.compactPanelButton} segment-manager-button-hug-icon20${
                              cprPixelBrushMode === 'draw' ? ' active' : ''
                            }`}
                            {...tooltipProps('Draw [E]')}
                            aria-label="Draw"
                            title="Draw"
                            aria-pressed={cprPixelBrushMode === 'draw'}
                            onClick={() => onCprPixelBrushModeChange('draw')}
                            startIcon={<BrushIcon />}
                          >
                            Brush [E]
                          </Button>

                          <Button
                            className={`${styles.compactPanelButton} segment-manager-button-hug-icon20${
                              cprPixelBrushMode === 'erase' ? ' active' : ''
                            }`}
                            {...tooltipProps('Erase [R]')}
                            aria-label="Erase"
                            title="Erase"
                            aria-pressed={cprPixelBrushMode === 'erase'}
                            onClick={() => onCprPixelBrushModeChange('erase')}
                            startIcon={<EraserIcon />}
                          >
                            Erase [R]
                          </Button>
                        </div>

                        <SizeControl
                          value={cprPixelBrushSize}
                          min={1}
                          max={30}
                          onChange={onCprPixelBrushSizeChange}
                        />
                      </div>
                    </div>
                  }
                </div>
              </>
            )} */}
          </div>
          {manualMode && (
            <div className={styles.actionSection}>
              {showCprModificationTools && <div className={styles.divider} />}

              <div
                className={`${styles.actionContainer} ${styles.compactActionContainer}`}
              >
                <Button
                  className={`${styles.compactTextActionButton} segment-manager-button-discard`}
                  startIcon={<CloseIcon />}
                  onClick={onOpenManualDiscard}
                >
                  Exit
                </Button>

                <Button
                  className={`${styles.compactTextActionButton} segment-manager-button-apply`}
                  onClick={onLoadDraft}
                >
                  Load
                </Button>

                <Button
                  className={`${styles.compactTextActionButton} segment-manager-button-apply`}
                  disabled={!canSaveDraft}
                  onClick={onSaveDraft}
                >
                  Save
                </Button>

                <Button
                  className={`${styles.compactTextActionButton} segment-manager-button-apply`}
                  disabled={!canApplyModification}
                  startIcon={<CircleCheckIcon />}
                  onClick={onOpenManualApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
